import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Calendar } from "lucide-react";
import { companyInfo } from "../data";
import { fetchPageBySlug } from "../lib/cms";

export default function Workshop() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug("vaerkstedet");
        setPageData(page);
      } catch (error) {
        console.error("Fejl ved indlæsning af værksted data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    document.title = "Om vores autoværksted i Skærbæk | Brdr. Lorenzen ApS";
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'På vores moderne service autoværksted i Skærbæk får du løst alle dine autorelaterede-opgaver. Læs mere og kontakt os her »');

    let ogImage = document.querySelector('meta[property="og:image"]');
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.setAttribute('content', '');
  }, []);

  return (
    <div className="bg-slate-50 text-slate-900 antialiased min-h-screen">
      {/* Header Banner */}
      <section className="relative overflow-hidden bg-slate-900 py-24 border-b border-slate-800">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/fab13f61-83de-491e-82c6-ef50b4ad367f.jpg"
            alt="Værkstedet"
            className="h-full w-full object-cover filter brightness-30 contrast-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-left">
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-4xl md:text-5xl text-white">
            {pageData?.title || "Værkstedet"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-300">
            Professionel reparation og service af alle bilmærker i Skærbæk. Over 40 års erfaring sikrer dig den højeste kvalitet.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Main Content (Verbatim from Scrape) */}
            <div className="lg:col-span-8 text-left space-y-8">
              {loading ? (
                <div className="space-y-4 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                  <div className="h-4 bg-slate-200 rounded w-4/5" />
                  <div className="h-4 bg-slate-200 rounded w-full" />
                </div>
              ) : (
                <>
                  {/* Main Image */}
                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-1.5 shadow-sm">
                    <img
                      src="https://assets.auxodev.dk/assets/845935e5-3993-406d-896b-e0a4423b6917/27e2baee-5b87-4eef-991b-90b19ff763d8.jpg"
                      alt="Værkstedet i Skærbæk"
                      className="rounded-lg w-full h-[300px] md:h-[400px] object-cover"
                    />
                  </div>

                  {/* Verbatim Content */}
                  <div
                    className="prose prose-slate max-w-none text-slate-600 leading-relaxed space-y-6"
                    dangerouslySetInnerHTML={{ __html: pageData?.content || "" }}
                  />
                </>
              )}
            </div>

            {/* Sidebar Column */}
            <div className="lg:col-span-4 space-y-8">
              {/* Quick Contact Card */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 md:p-8 text-left space-y-6 shadow-lg">
                <h3 className="text-base font-bold text-slate-900 border-b border-slate-200 pb-3">
                  Værkstedets Info
                </h3>

                <ul className="space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 shrink-0 text-blue-500" />
                    <div>
                      <p className="text-slate-900 font-semibold">{companyInfo.name}</p>
                      <p className="text-slate-600 mt-0.5">{companyInfo.address}</p>
                      <p className="text-slate-600">{companyInfo.zipCity}</p>
                    </div>
                  </li>
                  <li className="flex items-center gap-3">
                    <Phone className="h-5 w-5 shrink-0 text-blue-500" />
                    <a
                      href={`tel:${companyInfo.phone}`}
                      className="text-slate-900 hover:text-blue-600 transition-colors font-mono font-semibold"
                    >
                      {companyInfo.phoneFormatted}
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="h-5 w-5 shrink-0 text-blue-500" />
                    <a
                      href={`mailto:${companyInfo.email}`}
                      className="text-slate-900 hover:text-blue-600 transition-colors font-semibold"
                    >
                      {companyInfo.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-3 pt-4 border-t border-slate-200">
                    <Clock className="h-5 w-5 shrink-0 text-blue-500" />
                    <div className="space-y-1.5 w-full">
                      {companyInfo.openingHours.map((item) => (
                        <div key={item.days} className="flex justify-between text-xs">
                          <span className="text-slate-600">{item.days}</span>
                          <span className="text-slate-900 font-medium font-mono">{item.hours}</span>
                        </div>
                      ))}
                    </div>
                  </li>
                </ul>

                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <Link
                    to="/online-booking"
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 py-3 text-sm font-bold text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/10"
                  >
                    <Calendar className="h-4 w-4" />
                    Book Tid Online
                  </Link>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-200 bg-white py-3 text-sm font-bold text-slate-900 hover:bg-slate-50 transition-colors shadow-sm"
                  >
                    <Phone className="h-4 w-4 text-blue-500" />
                    Ring Op Nu
                  </a>
                </div>
              </div>

              {/* Trust Badge Card */}
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-left space-y-4 shadow-sm">
                <div className="flex items-center gap-3 text-blue-600">
                  <ShieldCheck className="h-6 w-6" />
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-wider">
                    Tryghedsgaranti
                  </h4>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Vi er medlem af Dansk Bilbrancheråd. Det er din garanti for, at arbejdet udføres efter de højeste faglige standarder, og at du beholder din fabriksgaranti.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
